/**
 * Drawer for a single webhook delivery.
 *
 * Shows the envelope (event type + ids + timestamps), every attempt
 * (request + response + latency + error), and a Replay action.
 *
 * Renders inside a right-anchored Dialog because the app doesn't have
 * a dedicated drawer primitive yet; the Dialog primitive's overlay +
 * close affordances are the same UX we want, just wider and right-
 * aligned. If/when a real drawer primitive lands, drop this in place.
 */

import { useState } from 'react'
import { Loader2, RefreshCcw } from 'lucide-react'
import { toast } from 'sonner'

import { useEventTypeLabel } from '@/admin/hooks/use-pulse-events'
import {
  usePulseWebhookDelivery,
  useReplayDelivery,
  type PulseDeliveryAttempt,
} from '@/admin/hooks/use-pulse-webhooks'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { toastApiError } from '@/shared/lib/api-error'
import { cn } from '@/shared/lib/utils'

export interface WebhookDeliveryDrawerProps {
  open: boolean
  webhookId: string
  deliveryId: string | null
  onClose: () => void
}

export function WebhookDeliveryDrawer({
  open,
  webhookId,
  deliveryId,
  onClose,
}: WebhookDeliveryDrawerProps) {
  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="sm:max-w-[680px]">
        <DialogTitle className="sr-only">Delivery detail</DialogTitle>
        {open && deliveryId && (
          <DrawerBody
            webhookId={webhookId}
            deliveryId={deliveryId}
            onClose={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

function DrawerBody({
  webhookId,
  deliveryId,
  onClose,
}: {
  webhookId: string
  deliveryId: string
  onClose: () => void
}) {
  const delivery = usePulseWebhookDelivery(webhookId, deliveryId)
  const replay = useReplayDelivery(webhookId)
  const labelFor = useEventTypeLabel()

  async function handleReplay() {
    try {
      const result = await replay.mutateAsync(deliveryId)
      toast.success(`Replay queued (delivery ${result.delivery_id.slice(0, 8)}...).`)
    } catch (err) {
      toastApiError(err, 'Failed to replay delivery.')
    }
  }

  if (delivery.isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-[var(--ink-3)]" />
      </div>
    )
  }

  if (delivery.error || !delivery.data) {
    return (
      <div className="py-12 text-center">
        <p className="text-[12.5px] text-[var(--ink-3)]">
          Could not load delivery details.
        </p>
      </div>
    )
  }

  const d = delivery.data
  const label = labelFor(d.event_type)

  return (
    <div className="max-h-[80vh] overflow-y-auto">
      {/* Header - Dialog primitive ships its own close X, no second one */}
      <div className="mb-4">
        <div className="mb-1.5 flex items-center gap-2 pr-8">
          <span className="text-[13px] font-medium text-[var(--ink)]">
            {label}
          </span>
          <DeliveryStatusBadge status={d.status} />
          {d.attempt_generation > 0 && (
            <Badge variant="outline" className="text-[var(--ink-3)]">
              Replay #{d.attempt_generation}
            </Badge>
          )}
        </div>
        <p className="truncate font-mono text-[11px] text-[var(--ink-3)]">
          del_{d.id}
        </p>
      </div>

      {/* Envelope */}
      <Section title="Envelope">
        <KV label="Event id">
          <code className="font-mono text-[11.5px]">evt_{d.event_id}</code>
        </KV>
        <KV label="Created">
          <span className="font-mono text-[11.5px]">{d.created_at}</span>
        </KV>
        {d.delivered_at && (
          <KV label="Delivered">
            <span className="font-mono text-[11.5px]">{d.delivered_at}</span>
          </KV>
        )}
        {d.rerun_of && (
          <KV label="Rerun of">
            <code className="font-mono text-[11.5px]">del_{d.rerun_of}</code>
          </KV>
        )}
      </Section>

      {/* Attempts */}
      <Section title={`Attempts (${d.attempts.length})`}>
        {d.attempts.length === 0 ? (
          <p className="px-3 py-3 text-[11.5px] text-[var(--ink-3)]">
            No attempts yet - delivery is still queued.
          </p>
        ) : (
          <div className="space-y-3">
            {d.attempts.map((a) => (
              <AttemptCard key={a.id} attempt={a} />
            ))}
          </div>
        )}
      </Section>

      {/* Footer actions */}
      <div className="mt-5 flex items-center justify-end gap-2 border-t-[0.5px] border-[var(--rule)] pt-4">
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
        <Button onClick={handleReplay} disabled={replay.isPending}>
          {replay.isPending ? (
            <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
          ) : (
            <RefreshCcw className="mr-1.5 h-3 w-3" />
          )}
          Replay
        </Button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Attempt card - one per delivery attempt
// ---------------------------------------------------------------------------

function AttemptCard({ attempt }: { attempt: PulseDeliveryAttempt }) {
  const [expanded, setExpanded] = useState(attempt.attempt_number === 0)
  const detail = attempt.attempt_detail || {}
  const request = (detail.request as Record<string, unknown> | undefined) ?? {}
  const response = (detail.response as Record<string, unknown> | undefined) ?? {}

  return (
    <div className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)]">
      <button
        type="button"
        onClick={() => setExpanded((x) => !x)}
        className="flex w-full cursor-pointer items-center gap-3 px-3 py-2.5 text-left"
      >
        <span
          className={cn(
            'inline-block h-1.5 w-1.5 shrink-0 rounded-full',
            attempt.success ? 'bg-[var(--green)]' : 'bg-[var(--red)]',
          )}
        />
        <span className="font-mono text-[11.5px] text-[var(--ink-2)]">
          Attempt #{attempt.attempt_number}
        </span>
        {typeof response.status === 'number' && (
          <Badge
            variant="outline"
            className={cn(
              'border-[var(--rule)] text-[var(--ink-2)]',
              attempt.success
                ? 'bg-[var(--green-bg)] text-[var(--green)]'
                : 'bg-[var(--red-bg)] text-[var(--red)]',
            )}
          >
            HTTP {response.status as number}
          </Badge>
        )}
        {attempt.latency_ms != null && (
          <span className="font-mono text-[11px] text-[var(--ink-3)]">
            {attempt.latency_ms} ms
          </span>
        )}
        <span className="ml-auto text-[11px] text-[var(--ink-3)]">
          {expanded ? 'Hide' : 'Show'}
        </span>
      </button>

      {expanded && (
        <div className="space-y-3 border-t-[0.5px] border-t-[var(--rule)] px-3 py-3">
          {attempt.error_detail && (
            <KV label="Error">
              <p className="whitespace-pre-wrap font-mono text-[11px] text-[var(--red)]">
                {attempt.error_detail}
              </p>
            </KV>
          )}
          {attempt.next_attempt_at && (
            <KV label="Next attempt at">
              <span className="font-mono text-[11px]">
                {attempt.next_attempt_at}
              </span>
            </KV>
          )}
          {Object.keys(request).length > 0 && (
            <BlobCard label="Request" blob={request} />
          )}
          {Object.keys(response).length > 0 && (
            <BlobCard label="Response" blob={response} />
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Reusable chrome
// ---------------------------------------------------------------------------

function Section({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="mb-5">
      <h3 className="mb-2 text-[10px] font-normal uppercase tracking-[0.16em] text-[var(--ink-3)]">
        {title}
      </h3>
      <div className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-2">
        {children}
      </div>
    </div>
  )
}

function KV({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[110px_1fr] items-start gap-3 border-b-[0.5px] border-[var(--rule-light)] py-1.5 last:border-b-0">
      <div className="pt-0.5 text-[10px] font-normal uppercase tracking-[0.16em] text-[var(--ink-3)]">
        {label}
      </div>
      <div className="text-[12px] text-[var(--ink)]">{children}</div>
    </div>
  )
}

function BlobCard({
  label,
  blob,
}: {
  label: string
  blob: Record<string, unknown>
}) {
  return (
    <div>
      <div className="mb-1 text-[10px] font-normal uppercase tracking-[0.16em] text-[var(--ink-3)]">
        {label}
      </div>
      <pre className="max-h-[260px] overflow-auto whitespace-pre-wrap break-all rounded-[4px] bg-[var(--bg-subtle)] p-2.5 font-mono text-[10.5px] leading-snug text-[var(--ink-2)]">
        {JSON.stringify(unpackJsonStrings(blob), null, 2)}
      </pre>
    </div>
  )
}

// Walk the blob and try to JSON.parse any string value that looks like
// JSON (starts with { or [). The webhook drainer stores `request.body`
// and `response.body` as the literal wire bytes, which for JSON
// payloads means a stringified-then-stringified blob. Parsing them out
// lets the formatter pretty-print the actual structure instead of
// rendering an escaped single-line string that needs horizontal scroll.
function unpackJsonStrings(value: unknown): unknown {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        return unpackJsonStrings(JSON.parse(trimmed))
      } catch {
        return value
      }
    }
    return value
  }
  if (Array.isArray(value)) return value.map(unpackJsonStrings)
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = unpackJsonStrings(v)
    }
    return out
  }
  return value
}

function DeliveryStatusBadge({ status }: { status: string }) {
  if (status === 'DELIVERED')
    return (
      <Badge
        variant="outline"
        className="border-[var(--status-ok-border)] bg-[var(--status-ok-bg)] text-[var(--status-ok)]"
      >
        Delivered
      </Badge>
    )
  if (status === 'PENDING')
    return (
      <Badge
        variant="outline"
        className="border-[var(--gold-border)] bg-[var(--gold-bg)] text-[var(--gold-ink)]"
      >
        Retrying
      </Badge>
    )
  if (status === 'SUPPRESSED')
    return (
      <Badge variant="outline" className="text-[var(--ink-3)]">
        Suppressed
      </Badge>
    )
  return <Badge variant="destructive">Failed</Badge>
}
